import React, { useState, useEffect, useRef } from 'react';
import { User, Mail, Phone, Shield, LogOut, Users, Loader2, Building2, ArrowRightLeft, BookOpen, Store, Settings } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { AuthService } from '../services/authService';
import { useNavigate, Link } from 'react-router-dom';
import { rrGetAttendanceToday, rrPunch } from '@/actions/rr';

interface UserProfileDropdownProps {
    trigger?: React.ReactNode;
    placement?: 'bottom-right' | 'right';
}

interface UserData {
    name: string;
    email: string;
    phone: string;
    role_name: string;
    team_name: string;
    is_super_admin: boolean;
    client_name: string;
}

const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({ trigger, placement = 'bottom-right' }) => {
    const navigate = useNavigate();
    const { userData: apiResponse, loading } = useUser();
    const isSuperAdmin = (apiResponse?.data as any)?.role?.role_id === 9401;
    const [isOpen, setIsOpen] = useState(false);
    const [userData, setUserData] = useState<UserData | null>(null);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [hasMultipleAccounts, setHasMultipleAccounts] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Transform API data whenever it changes
    useEffect(() => {
        if (apiResponse && apiResponse.data) {
            const apiData = apiResponse.data;
            // Transform API data to our format
            const transformedData: UserData = {
                name: apiData.userName,
                email: apiData.userEmail,
                phone: apiData.phone,
                role_name: apiData.role?.role_name || 'No Role',
                team_name: apiData.team?.team_name || 'No Team',
                is_super_admin: apiData.role?.role_name?.toLowerCase().includes('admin') || false,
                client_name: apiData.client?.client_name || 'No Organization'
            };
            setUserData(transformedData);

            // Check if user has multiple accounts using accounts_count from /me endpoint
            const accountsCount = apiData.accounts_count || 0;
            setHasMultipleAccounts(accountsCount > 1);
        }
    }, [apiResponse]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleLogout = async () => {
        try {
            setIsLoggingOut(true);

            // Punch out if currently punched in
            try {
                const todayRes = await rrGetAttendanceToday();
                const td = todayRes?.data as { status?: string; punch_out?: string | null } | null;
                const isPunchedIn = td?.status === 'present' && !td?.punch_out;
                if (isPunchedIn) await rrPunch();
            } catch { /* best-effort */ }

            // Clear punch-in session key so modal fires on next login
            sessionStorage.removeItem('zh:attendance:punch_shown');

            const logoutRedirectUrl = import.meta.env.VITE_LOGOUT_REDIRECT_URL || '/login';
            await AuthService.logout({
                clearLocalStorage: true,
                clearSessionStorage: false,
                clearIndexedDB: false,
                notifyBackend: true,
                redirectTo: logoutRedirectUrl
            });
        } catch (error) {
            console.error('Logout failed:', error);
            const logoutRedirectUrl = import.meta.env.VITE_LOGOUT_REDIRECT_URL || '/login';
            window.location.href = logoutRedirectUrl;
        } finally {
            setIsLoggingOut(false);
        }
    };

    if (loading) {
        return (
            <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse" />
        );
    }

    return (
        <>
        <div className="relative" ref={dropdownRef}>
            {trigger ? (
                <div onClick={() => setIsOpen(!isOpen)}>
                    {trigger}
                </div>
            ) : (
                <button
                    onClick={() => {
                        setIsOpen(!isOpen);
                    }}
                    className="w-10 h-10 bg-brandColor rounded-full flex items-center justify-center transition-all duration-200 cursor-pointer hover:opacity-90 hover:shadow-lg transform hover:scale-110 border-2 border-white"
                    type="button"
                >
                    <User size={20} className="text-white" />
                </button>
            )}

            {isOpen && (
                <div className={`absolute w-80 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-[99999] ${placement === 'right' ? 'left-full bottom-0 ml-2' : 'right-0 top-full mt-2'}`}>
                    {/* Header with brand background */}
                    <div className="bg-brandColor p-4">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                                <User size={24} className="text-white" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-lg text-white">{userData?.name || 'Guest User'}</h3>
                                <p className="text-xs text-white/90">{userData?.role_name || 'No role assigned'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Current Account/Client Section */}
                    {userData?.client_name && (
                        <div className="bg-green-50 border-b-2 border-green-100 px-4 py-3">
                            <div className="flex items-center space-x-2.5">
                                <div className="w-8 h-8 bg-brandColor rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Building2 size={16} className="text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Current Account</p>
                                    <p className="text-sm font-semibold text-gray-900 truncate">{userData.client_name}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* User Information */}
                    <div className="p-4 space-y-3">
                        {userData ? (
                            <>
                                <div className="flex items-center space-x-3 text-gray-700">
                                    <Mail size={16} className="text-gray-400" />
                                    <span className="text-sm">{userData.email}</span>
                                </div>

                                {userData.phone && (
                                    <div className="flex items-center space-x-3 text-gray-700">
                                        <Phone size={16} className="text-gray-400" />
                                        <span className="text-sm">{userData.phone}</span>
                                    </div>
                                )}

                                <div className="flex items-center space-x-3 text-gray-700">
                                    <Users size={16} className="text-gray-400" />
                                    <span className="text-sm">{userData.team_name}</span>
                                </div>

                                <div className="flex items-center space-x-3 text-gray-700">
                                    <Shield size={16} className="text-gray-400" />
                                    <div className="flex-1">
                                        <span className="text-sm">{userData.role_name}</span>
                                        {userData.is_super_admin && (
                                            <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                                Admin
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="text-sm text-gray-500">No user data available</div>
                        )}
                    </div>


                    {/* Actions */}
                    <div className="border-t border-gray-200">
                        <Link
                            to="/settings/profile"
                            onClick={() => setIsOpen(false)}
                            className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2 transition-colors border-b border-gray-100"
                        >
                            <Settings size={16} className="text-gray-400" />
                            <span>Settings</span>
                        </Link>
                        {isSuperAdmin && (
                            <Link
                                to="/shopify-admin"
                                onClick={() => setIsOpen(false)}
                                className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2 transition-colors border-b border-gray-100"
                            >
                                <Store size={16} className="text-gray-400" />
                                <span>Shopify Admin</span>
                            </Link>
                        )}
                        {hasMultipleAccounts && (
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    navigate('/settings/accounts');
                                }}
                                className="w-full px-4 py-3 text-left text-sm text-brandColor hover:bg-green-50 flex items-center space-x-2 transition-colors border-t border-gray-100"
                            >
                                <ArrowRightLeft size={16} />
                                <span>Switch Account</span>
                            </button>
                        )}
                        <div className={`flex items-center ${hasMultipleAccounts ? 'border-t border-gray-100' : ''}`}>
                            <button
                                onClick={handleLogout}
                                disabled={isLoggingOut}
                                className="flex-1 px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoggingOut ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        <span>Signing out...</span>
                                    </>
                                ) : (
                                    <>
                                        <LogOut size={16} />
                                        <span>Sign out</span>
                                    </>
                                )}
                            </button>
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    navigate('/docs');
                                }}
                                className="px-4 py-3 text-sm text-brandColor hover:bg-green-50 flex items-center space-x-2 transition-colors border-l border-gray-100"
                                title="Documentation"
                            >
                                <BookOpen size={16} />
                                <span>Docs</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>

        {/* Logout overlay */}
        {isLoggingOut && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center">
                <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
                <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
                    <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg, #157a4e, #0f9d6e)' }} />
                    <div className="p-8 text-center">
                        <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center mx-auto mb-4">
                            <Loader2 className="w-7 h-7 text-brandColor animate-spin" />
                        </div>
                        <h3 className="text-lg font-extrabold text-gray-900">Signing you out…</h3>
                        <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                            Punching you out and ending your session.<br />
                            <span className="text-xs text-gray-400">See you tomorrow!</span>
                        </p>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

export default UserProfileDropdown;